import React, {useCallback} from 'react';
import {
    Alert,
    Col,
    Form as BootstrapForm,
    Row,
    Stack,
    Button,
} from 'react-bootstrap';
import {Field, Form, Formik, FormikErrors, useFormikContext} from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import {Appliance} from 'components/customer/Product/Appliance/entity/Appliance';
import {RANGEHOOD} from 'components/customer/Product/store/productApi';
import {FileUpload} from 'shared/components/FileUpload/FileUpload';
import CollapseContainer from 'components/customer/Job/components/CollapseContainer';
import {useAppSelector} from 'store/customer';
import {selectApplianceOpeningHeight} from 'components/customer/Product/store/productSlice';
import {CustomSelectWithBlur} from 'components/customer/Product/Appliance/CustomSelectWithBlur';

const validationSchema = Yup.object().shape({
    make: Yup.string().required('Brand is required'),
    model: Yup.string().required('Model is required'),
});

interface AddApplianceFormProps {
    name?: string;
    hideDialog?: () => void;
    makes: string[];
    overrideAdd?: boolean;
    heightSetCallback?: (value: number) => void;
}

type SetFieldValue = (
    field: string,
    value: unknown,
    shouldValidate?: boolean | undefined
) => Promise<void | FormikErrors<Appliance>>;

export const AddApplianceForm = ({
    hideDialog,
    makes,
    overrideAdd = false,
    heightSetCallback,
}: AddApplianceFormProps) => {
    const {values, setFieldValue} = useFormikContext<{appliance: Appliance}>();
    const applianceHeight = useAppSelector(selectApplianceOpeningHeight);

    const handleSubmit = useCallback(
        (values: Appliance) => {
            values.externalCode = `${values.make} ${values.model}`;
            values.height = applianceHeight;
            void setFieldValue('appliance', values);

            if (heightSetCallback) {
                heightSetCallback(applianceHeight);
            }

            hideDialog && hideDialog();
        },
        [setFieldValue, hideDialog, applianceHeight, heightSetCallback]
    );

    const deleteFileCallback = useCallback(
        (setFieldValue: SetFieldValue) => () => {
            void setFieldValue('specPath', null);
        },
        []
    );

    return (
        <Formik
            initialValues={
                values.appliance && values.appliance.custom && !overrideAdd
                    ? values.appliance
                    : {
                          make: '',
                          model: '',
                          specPath: null,
                          custom: true,
                          externalCode: '',
                          type: RANGEHOOD,
                      }
            }
            validationSchema={validationSchema}
            onSubmit={handleSubmit}>
            {({values, errors, touched, setFieldValue}) => {
                return (
                    <CustomForm className="cbc-form">
                        <BootstrapForm.Group
                            as={Row}
                            className="mb-3"
                            controlId="make">
                            <StyledLabel column sm={3}>
                                Brand
                            </StyledLabel>
                            <Col sm={9}>
                                <CustomSelectWithBlur
                                    makes={makes.map((make) => ({
                                        label: make,
                                        value: make,
                                    }))}
                                />
                                <BootstrapForm.Control.Feedback
                                    type="invalid"
                                    className={
                                        touched.make && errors.make
                                            ? 'd-block'
                                            : undefined
                                    }>
                                    {errors.make}
                                </BootstrapForm.Control.Feedback>
                            </Col>
                        </BootstrapForm.Group>

                        <BootstrapForm.Group
                            as={Row}
                            className="mb-3"
                            controlId="appliance-model">
                            <StyledLabel column sm={3}>
                                Model
                            </StyledLabel>
                            <Col sm={9}>
                                <Field
                                    as={BootstrapForm.Control}
                                    type="text"
                                    name="model"
                                    placeholder="Model"
                                    isInvalid={Boolean(
                                        touched.model && errors.model
                                    )}
                                />
                                <BootstrapForm.Control.Feedback type="invalid">
                                    {errors.model}
                                </BootstrapForm.Control.Feedback>
                            </Col>
                        </BootstrapForm.Group>

                        <CollapseContainerLocal
                            initExpanded={true}
                            iconName="Cloud-Upload.svg"
                            headerText="File Upload"
                            infoText="Upload a file for your specified rangehood to assist with the custom manufacturing of this item. Accepted file formats: PDF, DOCX, TXT, PNG, or JPEG. Maximum file size: 5 MB.">
                            <FileUpload
                                fileUploadErrors={errors}
                                multiple={false}
                                attachedFiles={
                                    typeof values?.specPath == 'string'
                                        ? [String(values.specPath)]
                                        : []
                                }
                                name="specPath"
                                deleteAttachedFileCallback={deleteFileCallback(
                                    setFieldValue
                                )}
                                displayError={false}
                            />
                        </CollapseContainerLocal>

                        <Alert
                            variant="warning"
                            className="mt-3 mb-0 align-items-start">
                            <strong>WARNING&nbsp;:</strong>&nbsp;Custom
                            rangehoods may increase costs or delay processing of
                            your job.
                        </Alert>

                        <Line />

                        <Stack
                            direction="horizontal"
                            className="justify-content-between">
                            <ControlButton
                                variant="danger"
                                onClick={hideDialog}>
                                Cancel
                            </ControlButton>
                            <ControlButton variant="primary" type="submit">
                                Save
                            </ControlButton>
                        </Stack>
                    </CustomForm>
                );
            }}
        </Formik>
    );
};

export default AddApplianceForm;

export const CollapseContainerLocal = styled(CollapseContainer)`
    > div > img {
        width: 20px !important;
    }
`;

export const ControlButton = styled(Button)`
    border-radius: 10px;
    padding: 5px 10px;
    border: 0;
    font-size: 0.85em;
    font-weight: 500;
`;

const CustomForm = styled(Form)`
    padding: 0;
`;

const Line = styled.hr`
    border-color: #727273 !important;
    margin: 15px -30px;
`;

const StyledLabel = styled(BootstrapForm.Label)`
    font-weight: 600;
    color: ${({theme}) => theme.colors.primary?.main};
`;
